import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router'
import { useSessionStore } from '../../state'
import {
  calculateNextSubmissionNumber,
  writeAccreditationAcknowledgements
} from '../../services/acknowledgementService'
import routes from '../../pages/routes'

export interface TermsWizardProps {
  onComplete: () => void
}

interface WizardStep {
  label: string
  clauseTitle: string
  summary: string
  tosSection: string
}

const WIZARD_STEPS: WizardStep[] = [
  {
    label: 'Indemnity',
    clauseTitle: 'Indemnification (Section 4.2)',
    summary:
      'You agree to defend, indemnify, and hold harmless the Platform from and against any claims, liabilities, damages, losses, costs, or expenses (including legal fees) arising out of your breach of these Terms, your misuse of the Services, or your participation in any diving activity arranged via the Platform.',
    tosSection: '#4-limitation-of-liability--indemnification'
  },
  {
    label: 'Liability Disclaimer',
    clauseTitle: 'Disclaimer of Liability (Section 4.1)',
    summary:
      'To the maximum extent permitted by applicable law, the Platform shall not be liable for any direct, indirect, incidental, consequential, special, or punitive damages arising out of your use of the Services, the conduct of any user, any injury or loss sustained during a dive, or any reliance placed on user profiles or certification displays.',
    tosSection: '#4-limitation-of-liability--indemnification'
  },
  {
    label: 'User Conduct',
    clauseTitle: 'User Conduct (Section 5)',
    summary:
      'You agree not to provide false, misleading, or fraudulent accreditation or personal information; not to participate in dives beyond the limits of your actual training and physical condition; and not to use the Platform for any unlawful purpose. We reserve the right to suspend or terminate your account if you violate these Terms.',
    tosSection: '#5-user-conduct--misrepresentation'
  }
]

const TermsWizard = ({ onComplete }: TermsWizardProps) => {
  const user = useSessionStore(s => s.user)
  const [activeStep, setActiveStep] = useState(0)
  const [acknowledged, setAcknowledged] = useState<boolean[]>([
    false,
    false,
    false
  ])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAcknowledge = (stepIndex: number, checked: boolean) => {
    setAcknowledged(prev => {
      const next = [...prev]
      next[stepIndex] = checked
      return next
    })
  }

  const handleNext = () => {
    if (activeStep < WIZARD_STEPS.length - 1) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1)
    }
  }

  const handleComplete = async () => {
    if (!user?.uid || !user?.email) return

    setIsSubmitting(true)
    setError(null)

    try {
      const submissionNumber = await calculateNextSubmissionNumber(user.uid)
      await writeAccreditationAcknowledgements(
        user.uid,
        user.email,
        submissionNumber
      )
      onComplete()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save acknowledgements. Please try again.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentStep = WIZARD_STEPS[activeStep]
  const isLastStep = activeStep === WIZARD_STEPS.length - 1
  const canProceed = acknowledged[activeStep]

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Terms Acknowledgement
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Please review and acknowledge each clause below before proceeding with
        your accreditation submission.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {WIZARD_STEPS.map(step => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          {currentStep.clauseTitle}
        </Typography>
        <Typography variant='body2' sx={{ mb: 1 }}>
          {currentStep.summary}
        </Typography>
        <Link
          component={RouterLink}
          to={`${routes.termsOfService}${currentStep.tosSection}`}
          target='_blank'
          rel='noopener'
          variant='body2'
        >
          View full section
        </Link>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={acknowledged[activeStep]}
            onChange={e => handleAcknowledge(activeStep, e.target.checked)}
          />
        }
        label='I acknowledge and accept this clause'
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant='outlined'
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>

        {isLastStep ? (
          <Button
            variant='contained'
            onClick={handleComplete}
            disabled={!canProceed || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={!canProceed}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default TermsWizard
